import { Search } from '@mui/icons-material';
import { InputAdornment, TextField, MenuItem, OutlinedInput, Select } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-toastify';
import { Trash2 } from 'lucide-react';
import { useSelector } from 'react-redux';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import DetailUser from '@/components/Modal/User/DetailUser';
import CreateUser from '@/components/Modal/User/CreateUser';
import Delete from '@/components/Modal/General/Delete';
import Loader from '@/components/ui/Loader/Loader';

import usePatch from '@/hooks/usePatch';
import icAddNew from '@/assets/icons/add-new-user.png';
import user from '@/api/user';

const { getUsers } = user();

const activeList = [
	{ id: 1, name: 'Aktiv' },
	{ id: 2, name: 'Inaktiv' },
];

export default function Users() {
	const selectActiveRef = useRef(null);
	const user = useSelector((state) => state.auth.user);

	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [newUser, setNewUser] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [deleteUser, setDeleteUser] = useState(false);
	const [filterActiveUsers, setFilterActiveUser] = useState(1);

	const oC = useRef(null);

	const isAllowed = [1, 2].includes(user?.privilegelevelid);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		if (filterActiveUsers) {
			obj.status = filterActiveUsers === 1 ? [0,1] : [2,3];
		}
		return obj;
	}, [search, paginationModel, filterActiveUsers]);

	const {
		data: d,
		isFetching,
		isFetched,
		refetch,
	} = useQuery({
		queryKey: ['users', paginationModel, search, filterActiveUsers],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getUsers(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const { mutate: createUser } = usePatch({
		url: `api/auth/register`,
		method: 'post',
		callback: async (res) => {
			refetch();
			setNewUser(false);
			if ([200, 201].includes(res.status) && res.data) {
				toast.success(res.data?.message || `User ${res.status === 200 ? 'updated' : 'created'} successfully`);
			}
		},
		onError: (res) => {
			if (Array.isArray(res.data)) {
				res.data.forEach((err) => {
					if (typeof err === 'string') toast.error(err || `Error`);
				});
			}
		},
	});

	const dataCount =
		useMemo(() => {
			if (isFetched && d?.data?.count) {
				oC.current = d.data.count;
				return d.count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results || d?.data || [], [d]);

	let columns = [
		{
			field: 'id',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			renderCell: (index) => {
				const pM = paginationModel;
				const idx = index.api.getAllRowIds().indexOf(index.id) + 1;
				return idx + pM.page * pM.pageSize;
			},
		},
		{
			field: 'username',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'email',
			headerName: 'E-post',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'phonenumber',
			headerName: 'Telefonnummer',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'modify_by',
			headerName: 'Oppdatert av',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
			valueGetter: (_, row) => {
				return row?.modify_by ? row?.modify_by[0]?.email : '';
			},
		},
		{
			field: 'modify_date',
			headerName: 'Oppdatert dato',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'privilegeLevelID',
			headerName: 'Rolle',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
			valueGetter: (_, row) => {
				return row?.privilegelevel_detail[0]?.privilegelevelname || '';
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
			valueGetter: (_, row) => {
				return [0, 1].includes(+row?.status) ? "Aktiv" : "Inaktiv";
			},
		},
	];
	if (isAllowed) {
		columns.push({
			field: 'action',
			headerName: '',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (d) => {
				const { userid, email } = d.row;
				return (
					<button onClick={() => setDeleteUser({ id: userid, name: email })}>
						<Trash2 size={16} />
					</button>
				);
			},
		});
	}

	const onSaveUser = () => {
		refetch();
		setNewUser(false);
	};
	const onCreateUser = (data) => {
		createUser(data);
	};

	const {
		mutate: deleteUsers,
		isPending,
		isSuccess,
	} = usePatch({
		url: `usertabledoc/${deleteUser?.id}`,
		method: 'delete',
		callback: () => {
			toast.success(`brukere deleted`);
			refetch();
			setDeleteUser(false);
		},
		onError: () => {},
		showError: true,
	});

	const onDeleteUser = () => {
		if (!deleteUser) return;
		deleteUsers();
	};

	return (
		<>
			<BDHelmet title='Users' description='BDS Samferdsel users' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk'
							onChange={(e) => {
								setSearch(e.target.value);
								if (paginationModel.page !== 0) {
									setPaginationModel({ page: 0, pageSize: 10 });
								}
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
						<button
							onClick={() => setNewUser(true)}
							title='Registrer ny bruker'
							className='relative flex items-center justify-center gap-2 min-w-[208px] h-10 btn'
						>
							<div className='flex items-center justify-center w-5 h-5 bg-white rounded-full'>
								<img src={icAddNew} />
							</div>
							Registrer ny bruker
						</button>
					</div>
					<div className='flex justify-between'>
						<div>
							<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
							<span className='text-bd-gLight'>brukere</span>
						</div>
						<div className='flex items-center gap-1'>
							<span>Aktiv</span>

							<Select
								ref={selectActiveRef}
								displayEmpty
								labelId='active'
								id='active'
								className='min-w-48 select-default '
								value={filterActiveUsers}
								onChange={(e) => {
									const val = e.target.value;
									setFilterActiveUser(val)
									setPaginationModel((prevState) => {
										return {...prevState, page: 0}
									})
									selectActiveRef.current.blur();
								}}
								input={<OutlinedInput label='' size='small' placeholder='--Velg aktiv--' />}
								placeholder='--Velg aktiv--'
								MenuProps={{
									disableAutoFocus: true,
								}}
							>
								<MenuItem value="">
									<em className='text-gray-300' value=''>
										--Velg Active--
									</em>
								</MenuItem>
								{activeList?.map((p) => (
									<MenuItem key={p.id} value={p.id} name={p.name}>
										{p.name}
									</MenuItem>
								))}
							</Select>
						</div>
					</div>
					<div>
						{isPending && !isSuccess && <Loader />}

						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							// rowsPerPageOptions={[10, 20, 50]}
							className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							getRowId={(row) => row.userid}
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRow?.userid === params.row.userid) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
				</div>
				{!!selectedRow && (
					<DetailUser
						open={!!selectedRow}
						onSave={onSaveUser}
						data={selectedRow}
						onClose={() => setSelectedRow(false)}
						callback={refetch}
						isAllowed={isAllowed}
					/>
				)}
				{!!newUser && <CreateUser open={!!newUser} onSave={onCreateUser} onClose={() => setNewUser(false)} callback={refetch} />}
				{!!deleteUser && <Delete open={!!deleteUser} data={deleteUser} onSave={onDeleteUser} onClose={() => setDeleteUser(false)} />}
			</div>
		</>
	);
}
